import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useHub } from "../hooks/useHub";
import PrizeModal from "../components/PrizeModal";
import TabBar from "../components/TabBar";
import { useT } from "../i18n";

const MEDALS = ["🥇", "🥈", "🥉"];

// Публичная витрина призов: доступна и до регистрации (с экрана входа),
// и после — как вкладка таббара. Без телефона в URL таббар скрыт.
export default function Prizes() {
  const nav = useNavigate();
  const { t } = useT();
  const [params] = useSearchParams();
  const phone = params.get("phone") || "";

  // Для авторизованного гостя карточка Hero's Week живая: статус + кнопка «Забрать».
  const { hub, reload } = useHub(phone || null);
  const prize = hub?.prize ?? null;
  const isExistingClient = hub?.participant.isExistingClient ?? false;
  const [prizeOpen, setPrizeOpen] = useState(false);

  const places = [t("prizes.place1"), t("prizes.place2"), t("prizes.place3")];

  return (
    <div className={`min-h-dvh max-w-md mx-auto px-[22px] pt-4 ${phone ? "pb-28" : "pb-8"}`}>
      <header className="flex items-center gap-3 pt-1.5 mb-4">
        {!phone && (
          <button
            type="button"
            onClick={() => nav("/start")}
            aria-label={t("prizes.back")}
            className="w-9 h-9 rounded-full bg-white/[0.06] flex items-center justify-center active:bg-white/10 transition-colors shrink-0"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-[21px] font-extrabold tracking-tight leading-[1.1]">{t("prizes.title")}</h1>
          <p className="text-[13px] text-muted mt-[3px]">{t("prizes.subtitle")}</p>
        </div>
      </header>

      <div className="flex flex-col gap-3.5">
        {/* Когда проходят соревнования */}
        <section className="bg-card border border-line-2 rounded-[18px] px-4 py-3.5">
          <div className="text-[13px] font-bold text-white mb-1">📅 {t("prizes.schedule.title")}</div>
          <p className="text-[13px] text-muted leading-[1.5]">{t("prizes.schedule.body")}</p>
        </section>

        {/* Zone 1 — rowing podium */}
        {/* Главные призы фестиваля — самая яркая карточка страницы */}
        <section className="relative bg-card-2-grad border border-gold/40 rounded-[22px] p-5 overflow-hidden shadow-gold">
          <span
            className="absolute -right-10 -top-10 w-[140px] h-[140px] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(246,196,83,0.25), transparent 70%)" }}
          />
          <h2 className="relative text-[17px] font-extrabold text-white">{t("prizes.rowing.title")}</h2>
          <p className="relative text-[13px] text-muted mt-2 leading-[1.5]">{t("prizes.rowing.how")}</p>
          <div className="relative mt-2 flex gap-2">
            <span className="text-[11px] font-bold text-violet-lighter bg-violet-t14 px-2 py-0.5 rounded-md">
              {t("prizes.rowing.women")}
            </span>
            <span className="text-[11px] font-bold text-violet-lighter bg-violet-t14 px-2 py-0.5 rounded-md">
              {t("prizes.rowing.men")}
            </span>
          </div>
          <div className="relative mt-4 flex flex-col gap-2.5">
            {places.map((prize, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 rounded-[14px] px-3.5 py-3 ${
                  i === 0 ? "bg-gold/[0.14] border border-gold/50" : "bg-black/25"
                }`}
              >
                <span className={`leading-none shrink-0 ${i === 0 ? "text-3xl" : "text-2xl"}`}>{MEDALS[i]}</span>
                <span
                  className={`leading-[1.4] ${
                    i === 0 ? "text-[15px] font-extrabold text-gold-light" : "text-[14px] font-semibold text-white"
                  }`}
                >
                  {prize}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Hero's Week за участие — спокойнее главного приза (без свечения) */}
        <section className="relative bg-gold-card border border-gold/25 rounded-[22px] p-5 overflow-hidden">
          <span
            className="absolute -right-[30px] -top-10 w-[130px] h-[130px] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(246,196,83,0.3), transparent 70%)" }}
          />
          <div className="relative flex items-center gap-3">
            <span className="text-3xl leading-none shrink-0">🎁</span>
            <div>
              <h2 className="text-[17px] font-extrabold text-gold-light">
                {isExistingClient ? t("prizes.hwGift.title") : t("prizes.hw.title")}
              </h2>
              <p className="text-xs font-bold text-gold-deepest mt-0.5">{t("hub.prize.value")}</p>
            </div>
          </div>
          <p className="relative text-[13px] text-white/80 mt-3 leading-[1.5]">
            {isExistingClient ? t("prizes.hwGift.body") : t("prizes.hw.body")}
          </p>

          {/* Личный статус приза (только после входа) */}
          {prize &&
            (prize.granted ? (
              <p className="relative text-success text-sm font-medium mt-3">{t("hub.prize.granted")}</p>
            ) : prize.eligible ? (
              <button
                type="button"
                onClick={() => setPrizeOpen(true)}
                className="relative w-full h-[52px] mt-3 rounded-[15px] bg-gold-btn text-[#2A1E00] font-extrabold text-base active:opacity-90 transition-opacity shadow-gold-btn"
              >
                {isExistingClient ? t("prize.gift.claim") : t("hub.prize.claim")}
              </button>
            ) : (
              <p className="relative text-white/70 text-sm leading-relaxed mt-3">{t("hub.prize.locked")}</p>
            ))}
        </section>

        {/* Zone 2 — InBody */}
        <section className="bg-card border border-line-2 rounded-[22px] p-5">
          <h2 className="text-[17px] font-extrabold text-white">{t("prizes.inbody.title")}</h2>
          <p className="text-[13px] text-muted mt-2 leading-[1.5]">{t("prizes.inbody.body")}</p>
        </section>

        {/* Zone 3 — награда за клановые задания (сам список заданий — на вкладке «Кланы») */}
        <section className="bg-card border border-line-2 rounded-[22px] p-5">
          <h2 className="text-[17px] font-extrabold text-white">{t("prizes.clan.title")}</h2>
          <p className="text-[13px] text-muted mt-2 leading-[1.5]">{t("prizes.clan.body")}</p>
        </section>

        {/* Правила призов */}
        <section className="bg-card border border-line-2 rounded-[18px] px-4 py-3.5">
          <div className="text-[13px] font-bold text-white mb-1">{t("prizes.rules.title")}</div>
          <p className="text-[12px] text-muted leading-[1.5]">{t("prizes.rules.body")}</p>
        </section>
      </div>

      {prizeOpen && (
        <PrizeModal
          phone={phone}
          isExistingClient={isExistingClient}
          onClose={() => setPrizeOpen(false)}
          onGranted={reload}
        />
      )}

      {phone && <TabBar active="prizes" phone={phone} />}
    </div>
  );
}
